import { showLoginModal } from './auth.js';

export const API_BASE = window.CCPM_CONFIG?.apiBase ?? 'http://localhost:8001';

export async function apiFetch(path, options = {}) {
  // FormData сам выставляет Content-Type с boundary — нашим JSON-default'ом
  // ломаем multipart upload (Phase 5: /api/import/parse).
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers ?? {}),
  };
  let r;
  try {
    // credentials: 'include' — для JWT cookie (Phase 2 Wave 1).
    r = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  } catch (err) {
    throw new Error(`Нет соединения с сервером: ${err.message}`);
  }
  if (r.status === 401) {
    showLoginModal();
    throw new Error('401');
  }
  if (!r.ok) {
    let detail = null;
    try { detail = (await r.json())?.detail; } catch { /* no body */ }
    // Pydantic 422 шлёт detail как массив объектов [{loc, msg, ...}].
    // Сводим к строке для UI: «role: literal_error: ...».
    if (Array.isArray(detail)) {
      detail = detail
        .map(d => `${(d.loc ?? []).slice(1).join('.') || 'body'}: ${d.msg}`)
        .join('; ');
    } else if (detail && typeof detail === 'object') {
      detail = JSON.stringify(detail);
    }
    const err = new Error(`API ${r.status}: ${path}`);
    err.status = r.status;
    err.detail = detail;
    throw err;
  }
  if (r.status === 204) return null;
  return r.json();
}

const jsonBody = (b) => ({ body: JSON.stringify(b) });

export const api = {
  // ── reads ────────────────────────────────────────────────────────────────
  projects:     (params = {}) => apiFetch('/api/projects?' + new URLSearchParams(params)),
  tasks:        (params = {}) => apiFetch('/api/tasks?' + new URLSearchParams(params)),
  dependencies: (params = {}) => apiFetch('/api/dependencies?' + new URLSearchParams(params)),
  resources:    ()             => apiFetch('/api/resources'),
  // CRUD по ресурсам (миграция 048: rate_rub_per_hour, skills[], is_outsourceable).
  // Owner-only (require_action('resource.crud') на бэке).
  createResource: (body)        => apiFetch('/api/resources',         { method: 'POST',   ...jsonBody(body) }),
  patchResource:  (id, body)    => apiFetch(`/api/resources/${id}`,   { method: 'PATCH',  ...jsonBody(body) }),
  deleteResource: (id)          => apiFetch(`/api/resources/${id}`,   { method: 'DELETE' }),
  recurringBlocks: ()          => apiFetch('/api/resources/1/recurring'),
  me:           ()             => apiFetch('/api/me'),
  stats: {
    wip:           () => apiFetch('/api/stats/wip'),
    weeklyCapacity:() => apiFetch('/api/stats/weekly-capacity'),
    todayPriority: (params = {}) => apiFetch('/api/stats/today-priority?' + new URLSearchParams(params)),
    feverChart:    () => apiFetch('/api/stats/fever-chart'),
    personalSubstats: () => apiFetch('/api/stats/personal-substats'),
    // Калибрация: avg_ratio_pct, stddev_pct, sample_size, min/max_ratio_pct
    // (миграция 026 — корректная формула AVG(actual/estimate)).
    calibration:   (days = 90) => apiFetch('/api/stats/calibration?' + new URLSearchParams({ days })),
  },
  // Объём производства за период (Фаза 6 template-import).
  // params: { from, to, project_id?, resource_id? } — даты ISO YYYY-MM-DD.
  getVolume: (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') qs.set(k, v);
    }
    return apiFetch('/api/stats/volume?' + qs);
  },
  // Per-task таблица + тренд для страницы /calibration.
  calibration: {
    tasks: (params = {}) => apiFetch('/api/calibration/tasks?' + new URLSearchParams({ days: 90, limit: 50, ...params })),
    trend: (params = {}) => apiFetch('/api/calibration/trend?' + new URLSearchParams({ bucket: 'week', weeks: 8, ...params })),
  },
  resourceCalibration: (resourceId, days = 90) =>
    apiFetch(`/api/resources/${resourceId}/calibration?` + new URLSearchParams({ days })),
  pipeline:     () => apiFetch('/api/pipeline'),
  pipelineMove: (body) => apiFetch('/api/pipeline/move', { method: 'POST', ...jsonBody(body) }),

  // ── projects: writes ─────────────────────────────────────────────────────
  createProject:    (body)     => apiFetch('/api/projects',                       { method: 'POST',  ...jsonBody(body) }),
  patchProject:     (id, body) => apiFetch(`/api/projects/${id}`,                 { method: 'PATCH', ...jsonBody(body) }),
  // DELETE сейчас = soft cancel (status='cancelled'); UI использует cancelProject
  // с reason — ради audit-видимости. deleteProject держим как алиас на тот же
  // endpoint на случай будущего hard-delete.
  deleteProject:    (id)       => apiFetch(`/api/projects/${id}`,                 { method: 'DELETE' }),
  activateProject:  (id)       => apiFetch(`/api/projects/${id}/activate`,        { method: 'POST' }),
  pauseProject:     (id, body) => apiFetch(`/api/projects/${id}/pause`,           { method: 'POST', ...jsonBody(body ?? {}) }),
  resumeProject:    (id)       => apiFetch(`/api/projects/${id}/resume`,          { method: 'POST' }),
  cancelProject:    (id, body) => apiFetch(`/api/projects/${id}/cancel`,          { method: 'POST', ...jsonBody(body) }),
  completeProject: (id, body) =>
    apiFetch(`/api/projects/${id}/complete`, { method: 'POST', ...jsonBody(body) }),
  lockBaseline: (id) =>
    apiFetch(`/api/projects/${id}/lock-baseline`, { method: 'POST' }),
  unlockBaseline: (id) =>
    apiFetch(`/api/projects/${id}/unlock-baseline`, { method: 'POST' }),
  rebaselineProject: (id) =>
    apiFetch(`/api/projects/${id}/rebaseline`, { method: 'POST' }),
  // Phase E (план 2026-05-09): adaptive baseline с замочком
  toggleBaselineLock: (id) =>
    apiFetch(`/api/projects/${id}/baseline-lock`, { method: 'PATCH' }),
  recalculateBaseline: (id) =>
    apiFetch(`/api/projects/${id}/recalculate-baseline`, { method: 'POST' }),
  // Phase 3 «baseline edit mode» (план 2026-05-11): baseline → planned для
  // всех задач (вкл. done/in_progress). Возвращает {updated_count, breakdown}.
  resetToBaseline: (id) =>
    apiFetch(`/api/projects/${id}/reset-to-baseline`, { method: 'POST' }),
  // F12 (2026-05-10): явный пересчёт текущего плана. Existing endpoint
  // /recalculate уже вызывает _recalculate() (CC + buffer + recalculate_planned_dates).
  recalculatePlanned: (id) =>
    apiFetch(`/api/projects/${id}/recalculate`, { method: 'POST' }),

  // ── audit ────────────────────────────────────────────────────────────────
  audit: (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') qs.set(k, v);
    }
    return apiFetch('/api/audit?' + qs);
  },
  auditChangesSummary: (days = 7) =>
    apiFetch('/api/audit/changes-summary?' + new URLSearchParams({ days })),
  auditExportCsvUrl: (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') qs.set(k, v);
    }
    return `${API_BASE}/api/audit/export.csv?${qs}`;
  },

  // ── tasks ────────────────────────────────────────────────────────────────
  getTask:      (id)        => apiFetch(`/api/tasks/${id}`),
  // ── tasks: writes ────────────────────────────────────────────────────────
  createTask:   (body)      => apiFetch('/api/tasks',                  { method: 'POST',   ...jsonBody(body) }),
  patchTask:    (id, body)  => apiFetch(`/api/tasks/${id}`,            { method: 'PATCH',  ...jsonBody(body) }),
  deleteTask:   (id)        => apiFetch(`/api/tasks/${id}`,            { method: 'DELETE' }),
  startTask:    (id)        => apiFetch(`/api/tasks/${id}/start`,      { method: 'POST' }),
  completeTask: (id, body)  => apiFetch(`/api/tasks/${id}/complete`,   { method: 'POST',   ...jsonBody(body ?? {}) }),
  blockTask:    (id, body)  => apiFetch(`/api/tasks/${id}/block`,      { method: 'POST',   ...jsonBody(body) }),
  unblockTask:  (id)        => apiFetch(`/api/tasks/${id}/unblock`,    { method: 'POST' }),
  // F21 (2026-05-10): атомарная конверсия milestone↔task. Backend сам
  // обнуляет deadline/duration по chk_milestone_fields constraint.
  convertToTask:      (id) => apiFetch(`/api/tasks/${id}/to-task`,      { method: 'POST' }),
  convertToMilestone: (id) => apiFetch(`/api/tasks/${id}/to-milestone`, { method: 'POST' }),
  moveTask:     (id, body)  => apiFetch(`/api/tasks/${id}/move`,       { method: 'POST',   ...jsonBody(body) }),
  reorderTasks: (items)     => apiFetch('/api/tasks/reorder',          { method: 'POST',   ...jsonBody({ items }) }),

  // ── dependencies: writes ─────────────────────────────────────────────────
  postDependency:   (body)     => apiFetch('/api/dependencies',         { method: 'POST',   ...jsonBody(body) }),
  patchDependency:  (id, body) => apiFetch(`/api/dependencies/${id}`,   { method: 'PATCH',  ...jsonBody(body) }),
  deleteDependency: (id)       => apiFetch(`/api/dependencies/${id}`,   { method: 'DELETE' }),

  // ── analytics ─────────────────────────────────────────────────────────────
  resourceWorkload: (resourceId, start, end) =>
    apiFetch(`/api/resources/${resourceId}/workload?` + new URLSearchParams({ start, end })),
  bufferHistory: (projectId, days = 30) =>
    apiFetch(`/api/projects/${projectId}/buffer-history?` + new URLSearchParams({ days })),
  snapshotBufferState: () =>
    apiFetch('/api/admin/snapshot-buffer-state', { method: 'POST' }),

  // ── contractor updates (Phase 6 of contractor-bots plan) ─────────────────
  contractorUpdates: (status = 'pending_approval', limit = 50, offset = 0) =>
    apiFetch('/api/contractor-updates?' + new URLSearchParams({ status, limit, offset })),
  contractorUpdatesBadge: () => apiFetch('/api/contractor-updates/badge-count'),
  approveContractorUpdate: (id, body = {}) =>
    apiFetch(`/api/contractor-updates/${id}/approve`, { method: 'POST', ...jsonBody(body) }),
  rejectContractorUpdate: (id, body) =>
    apiFetch(`/api/contractor-updates/${id}/reject`, { method: 'POST', ...jsonBody(body) }),

  // ── cron heartbeats ──────────────────────────────────────────────────────
  cronHeartbeats: () => apiFetch('/api/cron/heartbeats'),

  // ── My Day (Кабинет сотрудника) ──────────────────────────────────────────
  // ── Comments + Notifications (миграция 050, MVP C1) ──────────────────────
  taskComments: {
    list:   (taskId)         => apiFetch(`/api/tasks/${taskId}/comments`),
    create: (taskId, body)   => apiFetch(`/api/tasks/${taskId}/comments`, { method: 'POST', ...jsonBody({ body }) }),
  },
  notifications: {
    list:        (params = {}) => apiFetch('/api/notifications?' + new URLSearchParams(params)),
    unreadCount: ()            => apiFetch('/api/notifications/unread-count'),
    markRead:    (id)          => apiFetch(`/api/notifications/${id}/read`, { method: 'POST' }),
    markAllRead: ()            => apiFetch('/api/notifications/mark-all-read', { method: 'POST' }),
  },

  myDay: {
    hero:       (taskId)    => apiFetch('/api/my-day/hero' + (taskId ? `?task_id=${taskId}` : '')),
    queue:      ()          => apiFetch('/api/my-day/queue'),
    production: (weeks = 12) => apiFetch('/api/my-day/production?' + new URLSearchParams({ weeks })),
    activity:   (limit = 15) => apiFetch('/api/my-day/activity?' + new URLSearchParams({ limit })),
    timerStop:  (body)      => apiFetch('/api/my-day/timer/stop', { method: 'POST', ...jsonBody(body) }),
    // Sessions (миграция 049): ▶/⏸/⏹ управление таймером сессии.
    sessionStart:  (taskId) => apiFetch(`/api/my-day/tasks/${taskId}/session/start`,  { method: 'POST' }),
    sessionPause:  (taskId) => apiFetch(`/api/my-day/tasks/${taskId}/session/pause`,  { method: 'POST' }),
    sessionResume: (taskId) => apiFetch(`/api/my-day/tasks/${taskId}/session/resume`, { method: 'POST' }),
    sessionStop:   (taskId, body = {}) => apiFetch(`/api/my-day/tasks/${taskId}/session/stop`, { method: 'POST', ...jsonBody(body) }),
    sessionDiscard:(taskId) => apiFetch(`/api/my-day/tasks/${taskId}/session/discard`, { method: 'POST' }),
  },

  // ── Рабочий стол (В1, backend/app/api/desk.py) ──────────────────────────
  desk: {
    summary: () => apiFetch('/api/desk/summary'),
  },

  // ── tenders «Аукционы» (A1, backend/app/api/tenders.py) ─────────────────
  sidebarCounts: () => apiFetch('/api/sidebar/counts'),
  tenders: {
    list: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null && v !== '') qs.set(k, v);
      }
      return apiFetch('/api/tenders?' + qs);
    },
    facets: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null && v !== '') qs.set(k, v);
      }
      return apiFetch('/api/tenders/facets?' + qs);
    },
    get:        (id)       => apiFetch(`/api/tenders/${id}`),
    patch:      (id, body) => apiFetch(`/api/tenders/${id}`,            { method: 'PATCH', ...jsonBody(body) }),
    // ТЕНД-ДИНАР-3: свободный комментарий по торгам (право tender.read, инлайн-правка в таблице).
    setComment: (id, comment) => apiFetch(`/api/tenders/${id}/comment`, { method: 'PATCH', ...jsonBody({ comment }) }),
    transition: (id, body) => apiFetch(`/api/tenders/${id}/transition`, { method: 'POST',  ...jsonBody(body) }),
    // Э-1: кнопки ведения этапов Динаром (Заявка подана / Итог торгов / Гарантия / Контракт).
    stageAction: (id, body) => apiFetch(`/api/tenders/${id}/stage-action`, { method: 'POST', ...jsonBody(body) }),
    decision:   (id, body) => apiFetch(`/api/tenders/${id}/decision`,   { method: 'POST',  ...jsonBody(body) }),
    // Ц-1: смена цены участия после решения (approved/docs, до срока подачи).
    price:      (id, body) => apiFetch(`/api/tenders/${id}/price`,      { method: 'PATCH', ...jsonBody(body) }),
    // М-1: «Как решали раньше» и статистика решений (owner).
    precedents: (id)       => apiFetch(`/api/tenders/${id}/precedents`),
    decisionStats: (days = 30) => apiFetch(`/api/tenders/decision-stats?days=${days}`),
    // ВОРОНКА: статистика «Директор → Воронка аукционов» (owner). params: {period, by}.
    funnel: (params = {}) => apiFetch('/api/tenders/funnel?' + new URLSearchParams(params)),
    estimate:   (id, body) => apiFetch(`/api/tenders/${id}/estimate`,   { method: 'POST',  ...jsonBody(body) }),
    // Д-1: обогащение с сайта МЭЛТОР делает worker; UI только ставит запрос в очередь.
    enrichRequest:    (id) => apiFetch(`/api/tenders/${id}/enrich-request`, { method: 'POST' }),
    enrichRequestAll: ()   => apiFetch('/api/tenders/enrich-request', { method: 'POST' }),
    // Х-2: договор и ТЗ вручную (файлы и архивы); doc_kind — contract | tz, если тип не распознан.
    uploadDocuments(id, files, docKind) {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      if (docKind) fd.append('doc_kind', docKind);
      return apiFetch(`/api/tenders/${id}/documents/upload`, { method: 'POST', body: fd });
    },
    deleteDocument: (id, docId) => apiFetch(`/api/tenders/${id}/documents/${docId}`, { method: 'DELETE' }),
    // Х-3: what — view (HTML, pdf inline или текст для iframe) | file (оригинал, attachment).
    documentUrl: (id, docId, what = 'view') => `${API_BASE}/api/tenders/${id}/documents/${docId}/${what}`,
    screen:     (id)       => apiFetch(`/api/tenders/${id}/screen`,     { method: 'POST' }),
    importMeltor: (file) => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      return apiFetch('/api/tenders/import/meltor', { method: 'POST', body: fd });
    },
    // Ф-1: первичный отбор (префильтр Руслана) — ручное решение и правила.
    prefilterOverride: (id, body) => apiFetch(`/api/tenders/${id}/prefilter/override`, { method: 'POST', ...jsonBody(body) }),
    prefilterRules:    ()         => apiFetch('/api/tenders/prefilter-rules'),
    createPrefilterRule: (body)   => apiFetch('/api/tenders/prefilter-rules', { method: 'POST', ...jsonBody(body) }),
    patchPrefilterRule:  (id, body) => apiFetch(`/api/tenders/prefilter-rules/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    rateCard:      ()     => apiFetch('/api/tenders/rate-card'),
    sectionShares: ()     => apiFetch('/api/tenders/section-shares'),
  },

  // ── expertise «Экспертиза» (ЭКС-1a, backend/app/api/expertise.py) ─────────
  expertise: {
    remarks: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null && v !== '') qs.set(k, v);
      }
      return apiFetch('/api/expertise/remarks?' + qs);
    },
    remarkFacets: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null && v !== '') qs.set(k, v);
      }
      return apiFetch('/api/expertise/remarks/facets?' + qs);
    },
    getRemark:    (id)       => apiFetch(`/api/expertise/remarks/${id}`),
    createRemark: (body)     => apiFetch('/api/expertise/remarks', { method: 'POST', ...jsonBody(body) }),
    patchRemark:  (id, body) => apiFetch(`/api/expertise/remarks/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    sections:     ()         => apiFetch('/api/expertise/sections'),
    sheets: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null && v !== '') qs.set(k, v);
      }
      return apiFetch('/api/expertise/sheets?' + qs);
    },
    sheet:        (id)       => apiFetch(`/api/expertise/sheets/${id}`),
    linkSheet:    (id, projectId) =>
      apiFetch(`/api/expertise/sheets/${id}`, { method: 'PATCH', ...jsonBody({ project_id: projectId }) }),
    sheetSuggestions: (id) => apiFetch(`/api/expertise/sheets/${id}/project-suggestions`),
    createSheet:  (body)     => apiFetch('/api/expertise/sheets', { method: 'POST', ...jsonBody(body) }),
    // ИЛ-1b: правка шапки листа (все поля 1С) и CRUD вкладок карточки.
    patchSheet:   (id, body) => apiFetch(`/api/expertise/sheets/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    sheetPlan:    (id)       => apiFetch(`/api/expertise/sheets/${id}/plan`),
    createSheetPlan: (id, body)   => apiFetch(`/api/expertise/sheets/${id}/plan`, { method: 'POST', ...jsonBody(body) }),
    patchSheetPlan:  (itemId, body) => apiFetch(`/api/expertise/plan/${itemId}`, { method: 'PATCH', ...jsonBody(body) }),
    deleteSheetPlan: (itemId)     => apiFetch(`/api/expertise/plan/${itemId}`, { method: 'DELETE' }),
    sheetContacts:   (id)    => apiFetch(`/api/expertise/sheets/${id}/contacts`),
    createSheetContact: (id, body)   => apiFetch(`/api/expertise/sheets/${id}/contacts`, { method: 'POST', ...jsonBody(body) }),
    patchSheetContact:  (itemId, body) => apiFetch(`/api/expertise/contacts/${itemId}`, { method: 'PATCH', ...jsonBody(body) }),
    deleteSheetContact: (itemId)     => apiFetch(`/api/expertise/contacts/${itemId}`, { method: 'DELETE' }),
    sheetComposition:   (id) => apiFetch(`/api/expertise/sheets/${id}/composition`),
    createSheetComposition: (id, body)   => apiFetch(`/api/expertise/sheets/${id}/composition`, { method: 'POST', ...jsonBody(body) }),
    patchSheetComposition:  (itemId, body) => apiFetch(`/api/expertise/composition/${itemId}`, { method: 'PATCH', ...jsonBody(body) }),
    deleteSheetComposition: (itemId)     => apiFetch(`/api/expertise/composition/${itemId}`, { method: 'DELETE' }),
    deleteRemark: (id)       => apiFetch(`/api/expertise/remarks/${id}`, { method: 'DELETE' }),
    import(files, dryRun) {
      const fd = new FormData();
      for (const f of files) fd.append('files', f, f.name);
      return apiFetch(`/api/expertise/import?dry_run=${dryRun ? 1 : 0}`, { method: 'POST', body: fd });
    },
    // ЭКС-1b-1: письма экспертизы (файлы у листа) и разбор на черновики.
    sheetFiles:   (sheetId)  => apiFetch(`/api/expertise/sheets/${sheetId}/files`),
    uploadSheetFile(sheetId, file, meta = {}) {
      const fd = new FormData();
      fd.append('file', file, file.name);
      for (const k of ['expertise_org', 'letter_no', 'letter_date', 'section_id']) {
        if (meta[k] != null && meta[k] !== '') fd.append(k, meta[k]);
      }
      return apiFetch(`/api/expertise/sheets/${sheetId}/files`, { method: 'POST', body: fd });
    },
    fileRemarks:  (fileId)   => apiFetch(`/api/expertise/files/${fileId}/remarks`),
    confirmFile:  (fileId, ids) =>
      apiFetch(`/api/expertise/files/${fileId}/confirm`, { method: 'POST', ...jsonBody({ ids: ids ?? null }) }),
    fileUrl:      (fileId)   => `${API_BASE}/api/expertise/files/${fileId}/file`,
    // ЭКС-1b-2: поставить письмо (PDF/нестандарт) в очередь ИИ-разбора.
    parseFile:    (fileId)   => apiFetch(`/api/expertise/files/${fileId}/parse`, { method: 'POST' }),
    // ЭКС-1b: разрешения на изменения (CRUD), статистика, сопоставление ФИО, образец.
    permits: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/expertise/permits?' + qs);
    },
    permitFacets: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/expertise/permits/facets?' + qs);
    },
    createPermit: (body)     => apiFetch('/api/expertise/permits', { method: 'POST', ...jsonBody(body) }),
    patchPermit:  (id, body) => apiFetch(`/api/expertise/permits/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    deletePermit: (id)       => apiFetch(`/api/expertise/permits/${id}`, { method: 'DELETE' }),
    stats: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/expertise/stats?' + qs);
    },
    people:    ()         => apiFetch('/api/expertise/people'),
    linkPerson: (body)    => apiFetch('/api/expertise/people/link', { method: 'POST', ...jsonBody(body) }),
    usersForLink: ()      => apiFetch('/api/expertise/users'),
    sampleUrl:  ()        => `${API_BASE}/api/expertise/sample`,
  },

  // ── Справочники контрагентов и людей (СПР-1a) ─────────────────────────────
  refs: {
    counterparties: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/refs/counterparties?' + qs);
    },
    counterpartyFacets: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/refs/counterparties/facets?' + qs);
    },
    counterparty:       (id)       => apiFetch(`/api/refs/counterparties/${id}`),
    createCounterparty: (body)     => apiFetch('/api/refs/counterparties', { method: 'POST', ...jsonBody(body) }),
    patchCounterparty:  (id, body) => apiFetch(`/api/refs/counterparties/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    deleteCounterparty: (id)       => apiFetch(`/api/refs/counterparties/${id}`, { method: 'DELETE' }),
    persons: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/refs/persons?' + qs);
    },
    person:        (id)       => apiFetch(`/api/refs/persons/${id}`),
    createPerson:  (body)     => apiFetch('/api/refs/persons', { method: 'POST', ...jsonBody(body) }),
    patchPerson:   (id, body) => apiFetch(`/api/refs/persons/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    deletePerson:  (id)       => apiFetch(`/api/refs/persons/${id}`, { method: 'DELETE' }),
    employees:     ()         => apiFetch('/api/refs/employees'),
    collect:       (apply)    => apiFetch(`/api/refs/collect?apply=${apply ? 1 : 0}`, { method: 'POST' }),
    // СПР-1b: слияние дублей ({id} — дубль, into_id — остаётся; apply=0 — предпросмотр).
    mergeCounterparty: (id, intoId, apply) =>
      apiFetch(`/api/refs/counterparties/${id}/merge?apply=${apply ? 1 : 0}`, { method: 'POST', ...jsonBody({ into_id: intoId }) }),
    mergePerson: (id, intoId, apply) =>
      apiFetch(`/api/refs/persons/${id}/merge?apply=${apply ? 1 : 0}`, { method: 'POST', ...jsonBody({ into_id: intoId }) }),
    // СПР-1b: сопоставление контактов листов/аукционов со справочником «Люди».
    matchContacts: (apply) => apiFetch(`/api/refs/match-contacts?apply=${apply ? 1 : 0}`, { method: 'POST' }),
  },

  // ── Нормативная база «Нормоконтроль» (НК-2a-1, backend/app/api/norms.py) ────
  norms: {
    docs: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/norms/docs?' + qs);
    },
    docFacets: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/norms/docs/facets?' + qs);
    },
    doc:        (id)       => apiFetch(`/api/norms/docs/${id}`),
    createDoc:  (body)     => apiFetch('/api/norms/docs', { method: 'POST', ...jsonBody(body) }),
    patchDoc:   (id, body) => apiFetch(`/api/norms/docs/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    deleteDoc:  (id)       => apiFetch(`/api/norms/docs/${id}`, { method: 'DELETE' }),
    versions:   (docId)    => apiFetch(`/api/norms/docs/${docId}/versions`),
    version:    (id)       => apiFetch(`/api/norms/versions/${id}`),
    uploadVersion(docId, file, meta = {}) {
      const fd = new FormData();
      fd.append('file', file, file.name);
      for (const k of ['label', 'effective_from']) {
        if (meta[k] != null && meta[k] !== '') fd.append(k, meta[k]);
      }
      return apiFetch(`/api/norms/docs/${docId}/versions`, { method: 'POST', body: fd });
    },
    // НОРМ-ТЕКСТ-1: пакетная загрузка текста — сопоставить файл с реестром по шифру и
    // загрузить редакцию (один файл за запрос; фронт шлёт файлы по одному).
    uploadMatch(file) {
      const fd = new FormData();
      fd.append('file', file, file.name);
      return apiFetch('/api/norms/upload-match', { method: 'POST', body: fd });
    },
    clauses: (versionId, params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch(`/api/norms/versions/${versionId}/clauses?` + qs);
    },
    versionText: (id)      => apiFetch(`/api/norms/versions/${id}/text`),
    // НК-2a-2: поиск по пунктам (межкументный) и пересчёт авто-карточек из замечаний
    search: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/norms/search?' + qs);
    },
    sync: () => apiFetch('/api/norms/sync-auto', { method: 'POST' }),
    // НОРМ-ПЕРЕРАЗБОР: пересчитать пункты текущих редакций из сохранённого текста
    resplit: () => apiFetch('/api/norms/resplit', { method: 'POST' }),
    // НК-2b-1: сверка актуальности — история, ручная отметка, запрос авто-проверки
    checks:       (docId)       => apiFetch(`/api/norms/docs/${docId}/checks`),
    addCheck:     (docId, body) => apiFetch(`/api/norms/docs/${docId}/checks`, { method: 'POST', ...jsonBody(body) }),
    checkRequest: (docId)       => apiFetch(`/api/norms/docs/${docId}/check-request`, { method: 'POST' }),
  },

  // ── ИИ-нормоконтроль разделов (НК-3a1, backend/app/api/normcheck.py) ────────
  normcheck: {
    jobs: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/normcheck/jobs?' + qs);
    },
    job:        (id) => apiFetch(`/api/normcheck/jobs/${id}`),
    // Загрузка раздела (НК-3a-5): form — { project_sheet_id, doc_kind, section_code,
    // section_name, executor_user_id, executor_person_id, executor_name, prev_job_id };
    // main/source — File[]. ИЛ выбирается явно (ref-picker), ИИ-распознавание штампа убрано.
    createJob(form, main, source = []) {
      const fd = new FormData();
      for (const [k, v] of Object.entries(form)) { if (v != null && v !== '') fd.append(k, v); }
      for (const f of main) fd.append('main', f, f.name);
      for (const f of source) fd.append('source_data', f, f.name);
      return apiFetch('/api/normcheck/jobs', { method: 'POST', body: fd });
    },
    addFiles(jobId, role, files) {
      const fd = new FormData();
      fd.append('role', role);
      for (const f of files) fd.append('files', f, f.name);
      return apiFetch(`/api/normcheck/jobs/${jobId}/files`, { method: 'POST', body: fd });
    },
    deleteJob:  (id) => apiFetch(`/api/normcheck/jobs/${id}`, { method: 'DELETE' }),
    // НК-3a-6: предшественники выбранного раздела по графу и их статус на этом ИЛ
    // (для мягкого предупреждения в форме загрузки). params: { project_sheet_id, section_template_id }.
    sectionDepsCheck: (params) =>
      apiFetch('/api/normcheck/section-deps-check?' + new URLSearchParams(params)),
    fileUrl:    (fileId) => `${API_BASE}/api/normcheck/files/${fileId}`,
    findings:   (jobId) => apiFetch(`/api/normcheck/jobs/${jobId}/findings`),
    createFinding: (jobId, body) => apiFetch(`/api/normcheck/jobs/${jobId}/findings`, { method: 'POST', ...jsonBody(body) }),
    patchFinding:  (id, body) => apiFetch(`/api/normcheck/findings/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    // Канбан-А: решение нормоконтролёра по всему разделу (перетаскивание на доске)
    jobDecision: (id, body) => apiFetch(`/api/normcheck/jobs/${id}/decision`, { method: 'PATCH', ...jsonBody(body) }),
    // Разметка страниц (НК-3a2)
    pages:      (jobId) => apiFetch(`/api/normcheck/jobs/${jobId}/pages`),
    patchPage:  (id, body) => apiFetch(`/api/normcheck/pages/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    bulkPages:  (jobId, body) => apiFetch(`/api/normcheck/jobs/${jobId}/pages/bulk`, { method: 'POST', ...jsonBody(body) }),
    previewUrl: (pageId, size = 'small') => `${API_BASE}/api/normcheck/pages/${pageId}/preview?size=${size}`,
    // Паспорт проекта (НК-3a2)
    factKeys:   () => apiFetch('/api/normcheck/fact-keys'),
    createFactKey: (body) => apiFetch('/api/normcheck/fact-keys', { method: 'POST', ...jsonBody(body) }),
    facts:      (sheetId) => apiFetch(`/api/normcheck/sheets/${sheetId}/facts`),
    createFact: (sheetId, body) => apiFetch(`/api/normcheck/sheets/${sheetId}/facts`, { method: 'POST', ...jsonBody(body) }),
    patchFact:  (id, body) => apiFetch(`/api/normcheck/facts/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    deleteFact: (id) => apiFetch(`/api/normcheck/facts/${id}`, { method: 'DELETE' }),
    // ИИ-проверка (НК-3b)
    analyze:    (jobId) => apiFetch(`/api/normcheck/jobs/${jobId}/analyze`, { method: 'POST' }),
    fragments:  (jobId) => apiFetch(`/api/normcheck/jobs/${jobId}/fragments`),
    allFindings: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/normcheck/findings?' + qs);
    },
    // Сверка паспорта, сравнение версий, метрики, промпты, статистика (НК-3c)
    reconcile:  (sheetId) => apiFetch(`/api/normcheck/sheets/${sheetId}/reconcile`, { method: 'POST' }),
    versionCompare: (jobId) => apiFetch(`/api/normcheck/jobs/${jobId}/version-compare`),
    metrics:    () => apiFetch('/api/normcheck/metrics'),
    prompts:    () => apiFetch('/api/normcheck/prompts'),
    createPrompt: (body) => apiFetch('/api/normcheck/prompts', { method: 'POST', ...jsonBody(body) }),
    patchPrompt: (id, body) => apiFetch(`/api/normcheck/prompts/${id}`, { method: 'PATCH', ...jsonBody(body) }),
    executorStats: (params = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null && v !== '') qs.set(k, v); }
      return apiFetch('/api/normcheck/executor-stats?' + qs);
    },
  },

  // ── users (Phase 2 Wave 2 admin panel) ───────────────────────────────────
  users:           (params = {}) => apiFetch('/api/users?' + new URLSearchParams(params)),
  createUser:      (body)        => apiFetch('/api/users', { method: 'POST',  ...jsonBody(body) }),
  patchUser:       (id, body)    => apiFetch(`/api/users/${id}`, { method: 'PATCH', ...jsonBody(body) }),
  deleteUser:      (id)          => apiFetch(`/api/users/${id}`, { method: 'DELETE' }),
  // Phase 2 Wave 3: список contractors для admin picker (owner-only).
  contractors:     (params = {}) => apiFetch('/api/contractors?' + new URLSearchParams(params)),
  // Phase 2 Wave 6: расширенный read-only список для /admin → Подрядчики.
  contractorsAdminList: (includeInactive = false) =>
    apiFetch('/api/contractors/admin-list?' + new URLSearchParams({
      include_inactive: includeInactive ? '1' : '0',
    })),

  // ── portfolios ────────────────────────────────────────────────────────────
  portfolios:      ()      => apiFetch('/api/portfolios'),
  createPortfolio: (body)  => apiFetch('/api/portfolios', { method: 'POST',  ...jsonBody(body) }),
  patchPortfolio:  (id, b) => apiFetch(`/api/portfolios/${id}`, { method: 'PATCH', ...jsonBody(b) }),
  deletePortfolio: (id)    => apiFetch(`/api/portfolios/${id}`, { method: 'DELETE' }),

  // ── permissions matrix (Phase 2 Wave 4, owner-only) ─────────────────────
  permissionsMatrix: () => apiFetch('/api/permissions/matrix'),
  patchPermissionsMatrix: (updates) =>
    apiFetch('/api/permissions/matrix', { method: 'PATCH', ...jsonBody({ updates }) }),
  portfolioVisibility: (pid) => apiFetch(`/api/portfolios/${pid}/visibility`),
  patchPortfolioVisibility: (pid, rows) =>
    apiFetch(`/api/portfolios/${pid}/visibility`, { method: 'PATCH', ...jsonBody({ rows }) }),

  // ── section library + template import (Phase 5) ─────────────────────────
  listSections:    (params = {}) => apiFetch('/api/sections?' + new URLSearchParams(params)),
  getSectionDetail: (id)         => apiFetch(`/api/sections/${id}`),
  parseImport:     (files) => {
    // multipart/form-data — apiFetch автоматически опускает JSON-Content-Type
    // когда body=FormData (boundary ставит браузер).
    const fd = new FormData();
    for (const f of files) fd.append('files', f, f.name);
    return apiFetch('/api/import/parse', { method: 'POST', body: fd });
  },
  instantiateImport: (body) =>
    apiFetch('/api/import/instantiate', { method: 'POST', ...jsonBody(body) }),

  // ── project documents (Phase A: модалка создания проекта с 5 слотами) ──
  listProjectDocuments: (projectId) =>
    apiFetch(`/api/projects/${projectId}/documents`),
  uploadProjectDocument: (projectId, docType, file) => {
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file, file.name);
    return apiFetch(`/api/projects/${projectId}/documents`, { method: 'POST', body: fd });
  },
  deleteProjectDocument: (projectId, docType) =>
    apiFetch(`/api/projects/${projectId}/documents/${docType}`, { method: 'DELETE' }),

  // ── per-project assignments ──────────────────────────────────────────────
  projectManagers:    (pid)         => apiFetch(`/api/projects/${pid}/managers`),
  addProjectManager:  (pid, userId) => apiFetch(`/api/projects/${pid}/managers`,
                                                 { method: 'POST',   ...jsonBody({ user_id: userId }) }),
  removeProjectManager: (pid, uid)  => apiFetch(`/api/projects/${pid}/managers/${uid}`, { method: 'DELETE' }),
  projectViewers:     (pid)         => apiFetch(`/api/projects/${pid}/viewers`),
  addProjectViewer:   (pid, userId) => apiFetch(`/api/projects/${pid}/viewers`,
                                                 { method: 'POST',   ...jsonBody({ user_id: userId }) }),
  removeProjectViewer: (pid, uid)   => apiFetch(`/api/projects/${pid}/viewers/${uid}`, { method: 'DELETE' }),
};
